import React, { useState, useCallback, useRef, useEffect } from 'react';
import { difference, pick } from 'lodash';

import { Container } from 'theme/layout';

import { SingleOption, OptionProps, OptionValue } from './option';

/**
 * Checks option from by previously entered answers
 *
 * @param payload -  Option payload
 * @param defaults - Previously entered state data
 */
function checkDefaults(payload: Record<string, string>, defaults: Record<string, string>) {
  const keys = Object.keys(payload);
  const size = keys.length;

  for (let i = 0; i < size; i++) {
    const key = keys[i];
    const isChecked = defaults[key] === payload[key];

    if (isChecked) {
      return true;
    }
  }
  return false;
}
/**
 * Displays a selector for multiple options and handles chat interactions.
 *
 * @param props - Multi Select Props.
 */
export function MultiSelect(props: Props) {
  const {
    maxSelection = 1,
    stretched,
    onUpdate,
    onValidate,
    options = [],
    defaults = {},
    defaultButtonText,
    onDefaultButtonVisibility,
    activeColor,
    iconColor,
  } = props;
  const [optionMap, setOptionMap] = useState(
    () =>
      new Map(
        options.map(option => {
          const { label, payload = {} } = option;

          option.checked = checkDefaults(payload, defaults);

          return [label, option];
        })
      )
  );
  const values = Array.from(optionMap.values());
  const lock = useRef(values.some(isExclusiveChecked));
  const numChecked = useRef(values.filter(option => option.checked).length);

  useEffect(() => {
    if (!onUpdate) {
      return;
    }

    let payload = {};
    let overrideValues = {};

    for (const value of optionMap.values()) {
      const { override = {}, ...rest } = value.payload;

      if (value.checked) {
        payload = {
          ...payload,
          ...rest,
        };
      }

      overrideValues = {
        ...override,
        ...overrideValues,
      };
    }
    const keys = Object.keys(payload);
    const overrideKeys = Object.keys(overrideValues);
    const inOverride = difference(overrideKeys, keys);
    const inPayload = difference(keys, overrideKeys);
    const usePayloadKeys = inPayload.length === 0 && keys.length > 0;
    const payloadKeys = usePayloadKeys ? keys : inPayload;

    payload = pick(payload, payloadKeys);
    overrideValues = pick(overrideValues, inOverride);
    payload = {
      ...payload,
      ...overrideValues,
    };

    onValidate(numChecked.current > 0);
    onDefaultButtonVisibility(false);
    if (defaultButtonText && numChecked.current <= 0) {
      onValidate(true);
      onDefaultButtonVisibility(true);
    }

    onUpdate(payload);
  }, [onUpdate, onValidate, optionMap]);
  const onSelect = useCallback(
    (option: MultiSelectOption) => {
      option.checked = !option.checked;
      lock.current = isExclusiveChecked(option);

      setOptionMap(updateOptionMap(option, numChecked, maxSelection));
    },
    [maxSelection]
  );
  const optionsArray = [...optionMap.values()];
  const optionsArrayLen = optionsArray.length;
  const hasIcons = Boolean(optionsArray.find(option => option.icon !== undefined));
  const optionsAlignment = hasIcons ? ['center', 'start'] : 'center';

  return (
    <Container
      flexDirection={['column', 'row']}
      justifyContent="center"
      alignItems={optionsAlignment}
    >
      {optionsArray.map((option, index) => {
        const disabled = isDisabled(numChecked.current, lock.current, option, maxSelection);
        const isLast = index === optionsArrayLen - 1;

        const marginBottom = [isLast ? '0px' : '10px', '0px'];
        const marginRight = hasIcons
          ? ['0px', isLast ? '0px' : '60px']
          : ['0px', isLast ? '0px' : '10px'];

        return (
          <Container key={option.label} marginBottom={marginBottom} marginRight={marginRight}>
            <SingleOption
              block={hasIcons}
              disabled={disabled}
              onSelect={onSelect}
              option={option}
              stretched={stretched}
              maxSelection={maxSelection}
              numChecked={numChecked.current}
              activeColor={activeColor}
              iconColor={iconColor}
            />
          </Container>
        );
      })}
    </Container>
  );
}
/**
 * Determines if the provided option is exclusive and if it is checked.
 *
 * @param option - Current option to evaluate.
 */
function isExclusiveChecked(option: MultiSelectOption): boolean {
  return option.optionType === 'exclusive' && option.checked;
}
/**
 * Updates the state of options for selection behaviours.
 *
 * @example
 * "maxSelection" set to 1, will cause radio button like behaviour
 *
 * @param option - Option that was pressed by the user.
 * @param numChecked - Number of options currently checked.
 * @param maxSelection - Maximum number of selectable options allowed.
 */
function updateOptionMap(
  option: MultiSelectOption,
  numChecked: React.MutableRefObject<number>,
  maxSelection: number
): React.SetStateAction<Map<string, MultiSelectOption>> {
  return map => {
    const isExclusive = option.optionType === 'exclusive';
    const isOtherChecked = !isExclusive && option.checked;

    for (const [key, val] of map.entries()) {
      // Skip self and skip unchecked options
      if (key === option.label || !val.checked) {
        continue;
      }

      // Clear checked fields if max reached,
      // exclusive checked, or other checked while exclusive checked
      if (
        (numChecked.current >= maxSelection && option.checked) ||
        isExclusiveChecked(option) ||
        (isOtherChecked && val.optionType === 'exclusive')
      ) {
        // Adjust number of currently checked options
        numChecked.current -= numChecked.current <= 0 ? 0 : 1;
        map.set(key, { ...val, checked: false });
      }
    }
    numChecked.current += option.checked ? 1 : -1;
    map.set(option.label, { ...option });

    return new Map(map);
  };
}
/**
 * Determines the lock state of an option.
 *
 * @param numChecked - The number of options currently checked.
 * @param lockChecked - Should be true if the exclusive option is checked.
 * @param option - The option to assess the lock for.
 * @param maxSelection - Maximum number of options allowed to be selected. Default [0] Infinite options.
 * @returns True if option should be disabled.
 */
function isDisabled(
  numChecked: number,
  lockChecked: boolean,
  option: MultiSelectOption,
  maxSelection?: number
) {
  const isExclusive = option.optionType === 'exclusive';
  const disableExclusive = isExclusive && numChecked > 0 && !lockChecked;
  const disableOthers =
    (lockChecked && !isExclusive) || (numChecked >= maxSelection && !option.checked);

  return disableExclusive || disableOthers;
}
/* Type definitions */
type MultiSelectOption = OptionValue<Record<string, string>>;

// extends NakedComponentProps
export interface Props extends Omit<OptionProps<Record<string, string>>, 'option' | 'onSelect'> {
  /** Maximum number of options you can select. */
  maxSelection?: number;
  /** Default text for action button */
  defaultButtonText?: string;
  /** List of options to display on the multi select. */
  options: MultiSelectOption[];
  /** Handles `onUpdate` events fired by Chat Components */
  onUpdate?(payload: Record<string, unknown>): void;
  /** Emits on `onValidate` event */
  onValidate(isValid: boolean): void;
  /** Emits the visibility state if the default button */
  onDefaultButtonVisibility?(isVisible: boolean): void;
  /** */
  defaults?: Record<string, string>;
  /** Icon color with which we override the main color*/
  iconColor?: string;
  /** Active color that will override the main primary color */
  activeColor?: string;
}
